"use client"

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Checkbox } from "@/components/ui/checkbox"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import StudentSelect from "./StudentSelect"
import { ArrowUpDown, ChevronDown, ChevronUp, Clock, Search, Filter, UserCheck, Upload, X } from "lucide-react"
import ClassroomMappingImport from "./ClassroomMappingImport"
import Pagination from "../../Common/Pagination"
import { useClassroomMapping, getFullName, getInitials } from "./hooks/useClassroomMapping"

function ClassroomMapping() {
  const {
    teachers, students, classes, sections, teacherMappings,
    selectedTeacher, currentTeacherClass, setCurrentTeacherClass,
    currentTeacherSection, setCurrentTeacherSection,
    teacherType, setTeacherType,
    selectedStudent, currentStudentClass, setCurrentStudentClass,
    currentStudentSection, setCurrentStudentSection,
    selectedSubjects, filteredSubjects,
    error, isLoading, isSuccess,
    viewMode, setViewMode,
    teacherAvailableSections, studentAvailableSections,
    activeTab, setActiveTab,
    searchQuery, searchSuggestions, showSuggestions,
    sortDirection, sortColumn,
    teacherPage, setTeacherPage,
    studentPage, setStudentPage,
    itemsPerPage, setItemsPerPage,
    teacherTotal, studentTotal,
    sortedTeachers, sortedStudents,
    handleTeacherChange, handleStudentChange,
    saveTeacherMapping, saveStudentMapping,
    getTeacherMappings, getStudentMappings, getClassSectionMappings,
    handleDeleteMapping,
    handleTeacherClick, handleStudentClick,
    handleSearchChange, handleSuggestionClick,
    handleSort, toggleSubject,
    setSortColumn, setSortDirection,
  } = useClassroomMapping()

  const getSortIcon = (column) => {
    if (column !== sortColumn) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />
    return sortDirection === "asc"
      ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
      : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />
  }

  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-[#0263cc] text-xl font-semibold">Classroom Mapping</CardTitle>
              <CardDescription>Manage teacher and student class assignments</CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="relative w-full sm:w-auto">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder={`Search ${activeTab === "teachers" ? "teachers" : "students"}...`}
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                />
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                    {searchSuggestions.map((person) => (
                      <div key={person._id} className="p-2 hover:bg-gray-100 cursor-pointer" onClick={() => handleSuggestionClick(person)}>
                        {person.firstName} {person.lastName}{person.admissionNumber ? ` - ${person.admissionNumber}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-9 w-9 p-0 border-[#0263cc]/20">
                    <Filter className="h-4 w-4" />
                    <span className="sr-only">Filter</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px]">
                  <DropdownMenuLabel>Sort By</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { setSortColumn("name"); setSortDirection("asc") }}>Name (A to Z)</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setSortColumn("name"); setSortDirection("desc") }}>Name (Z to A)</DropdownMenuItem>
                  {activeTab === "students" && (
                    <>
                      <DropdownMenuItem onClick={() => { setSortColumn("admissionNo"); setSortDirection("asc") }}>Admission No. (Ascending)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setSortColumn("admissionNo"); setSortDirection("desc") }}>Admission No. (Descending)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setSortColumn("class"); setSortDirection("asc") }}>Class (Ascending)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setSortColumn("class"); setSortDirection("desc") }}>Class (Descending)</DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <Tabs defaultValue="teachers" value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6 grid grid-cols-3 gap-2 bg-white p-1">
              <TabsTrigger value="teachers" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap">
                Teacher Management
              </TabsTrigger>
              <TabsTrigger value="students" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap">
                Student Management
              </TabsTrigger>
              <TabsTrigger value="import" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white whitespace-nowrap flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Import
              </TabsTrigger>
            </TabsList>

            <TabsContent value="import" className="p-4">
              <ClassroomMappingImport />
            </TabsContent>

            {/* Teachers Tab */}
            <TabsContent value="teachers" className="p-0">
              <div className="p-4 bg-white border-b border-gray-100">
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Select Class</label>
                    <Select value={currentTeacherClass} onValueChange={setCurrentTeacherClass}>
                      <SelectTrigger className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Choose Class" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        {classes.map((cls) => <SelectItem key={cls._id} value={cls._id}>{cls.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Select Section</label>
                    <Select value={currentTeacherSection} onValueChange={setCurrentTeacherSection} disabled={!currentTeacherClass || teacherAvailableSections.length === 0}>
                      <SelectTrigger className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder={!currentTeacherClass ? "Select a class first" : teacherAvailableSections.length === 0 ? "No sections available" : "Choose Section"} />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        {teacherAvailableSections.map((s) => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Select Teacher</label>
                    <Select value={selectedTeacher} onValueChange={handleTeacherChange}>
                      <SelectTrigger className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Choose Teacher" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        {teachers.map((t) => <SelectItem key={t._id} value={t._id}>{getFullName(t)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Teacher Type</label>
                    <Select value={teacherType} onValueChange={setTeacherType}>
                      <SelectTrigger className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="class_teacher">Class Teacher</SelectItem>
                        <SelectItem value="subject_teacher">Subject Teacher</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium mb-2">Assign Subjects</label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full justify-start border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                          {selectedSubjects.length > 0 ? `${selectedSubjects.length} subject(s) selected` : "Select Subjects"}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-56 max-h-60 overflow-y-auto">
                        <DropdownMenuLabel>Subjects</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {filteredSubjects.map((subject) => (
                          <DropdownMenuItem key={subject._id} onSelect={(e) => e.preventDefault()} className="flex items-center space-x-2">
                            <Checkbox
                              id={`subject-${subject._id}`}
                              checked={selectedSubjects.includes(subject._id)}
                              onCheckedChange={() => toggleSubject(subject._id)}
                            />
                            <label htmlFor={`subject-${subject._id}`} className="cursor-pointer flex-1">{subject.name}</label>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex items-end md:col-span-1">
                    <Button
                      onClick={saveTeacherMapping}
                      className="w-full bg-[#0263cc] hover:bg-[#0263cc]/90 text-white"
                      disabled={!currentTeacherClass || !currentTeacherSection || !teacherType || !selectedTeacher || selectedSubjects.length === 0 || isLoading}
                    >
                      {isLoading ? <Clock className="mr-2 h-4 w-4 animate-spin" /> : isSuccess ? <UserCheck className="mr-2 h-4 w-4" /> : null}
                      {isLoading ? "Saving..." : isSuccess ? "Saved Successfully" : "Save Assignment"}
                    </Button>
                  </div>
                </div>

                {error && (
                  <Alert variant="destructive" className="mt-4">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
              </div>

              <div className="p-4 border-t border-gray-100">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Current Teacher Assignments</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-green-500"></div><span>Class Teacher</span></div>
                      <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-full bg-blue-500"></div><span>Subject Teacher</span></div>
                    </div>
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div><span className="font-medium text-blue-800">Total Teachers:</span><span className="ml-2 text-blue-600">{teachers.length}</span></div>
                        <div><span className="font-medium text-green-800">Assigned:</span><span className="ml-2 text-green-600">{new Set(teacherMappings.map((m) => m.user?._id).filter(Boolean)).size}</span></div>
                        <div><span className="font-medium text-orange-800">Unassigned:</span><span className="ml-2 text-orange-600">{teachers.length - new Set(teacherMappings.map((m) => m.user?._id).filter(Boolean)).size}</span></div>
                        <div><span className="font-medium text-purple-800">Total Assignments:</span><span className="ml-2 text-purple-600">{teacherMappings.length}</span></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">View by:</span>
                    <Select value={viewMode} onValueChange={setViewMode}>
                      <SelectTrigger className="w-[150px] border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="teacher">Teachers</SelectItem>
                        <SelectItem value="class">Classes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  {viewMode === "teacher" ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 hover:bg-gray-50">
                          <TableHead className="cursor-pointer" onClick={() => handleSort("name")}>
                            <div className="flex items-center">Name{getSortIcon("name")}</div>
                          </TableHead>
                          <TableHead>Assignments</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedTeachers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center py-8 text-gray-500">
                              {searchQuery ? "No teachers found matching your search" : "No teachers available"}
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedTeachers.map((teacher) => {
                            const mappings = getTeacherMappings(teacher._id)
                            return (
                              <TableRow key={teacher._id} className="hover:bg-gray-50 transition-colors">
                                <TableCell>
                                  <div className="flex items-center space-x-3">
                                    {teacher.profileImage ? (
                                      <img src={teacher.profileImage || "/placeholder.svg"} alt={getFullName(teacher)} className="h-8 w-8 rounded-full object-cover" />
                                    ) : (
                                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-600 text-sm font-medium">{getInitials(teacher)}</span>
                                      </div>
                                    )}
                                    <span className="text-[#0263cc] hover:text-[#0263cc]/80 cursor-pointer font-medium" onClick={() => handleTeacherClick(teacher)}>
                                      {getFullName(teacher)}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {mappings.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                      {(() => {
                                        const grouped = mappings.reduce((acc, mapping) => {
                                          const key = `${mapping.class?._id}-${mapping.section?._id}`
                                          if (!acc[key]) acc[key] = { class: mapping.class, section: mapping.section, type: mapping.type, subjects: [], mappingId: mapping._id }
                                          if (Array.isArray(mapping.subjects)) {
                                            acc[key].subjects.push(...mapping.subjects.filter((s) => s && s._id).map((s) => s.name || "Unknown Subject"))
                                          }
                                          return acc
                                        }, {})
                                        return Object.values(grouped).map((group, i) => (
                                          <Badge key={`${group.class?._id}-${group.section?._id}-${i}`} className={`${group.type === "class_teacher" ? "bg-green-500 hover:bg-green-600" : "bg-blue-500 hover:bg-blue-600"} text-white flex items-center gap-2 px-2 py-1`}>
                                            <span>{group.class?.name || "Unknown"} {group.section?.name || ""} - {group.subjects.join(", ")}</span>
                                            <button onClick={(e) => { e.stopPropagation(); handleDeleteMapping(group.mappingId, "teacher", `${getFullName(teacher)} (${group.class?.name}-${group.section?.name})`) }} className="ml-1 hover:bg-red-500 rounded-full p-1 transition-all inline-flex items-center justify-center hover:scale-110" title="Delete assignment">
                                              <X className="h-4 w-4 text-white" />
                                            </button>
                                          </Badge>
                                        ))
                                      })()}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            )
                          })
                        )}
                      </TableBody>
                    </Table>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 hover:bg-gray-50">
                          <TableHead>Class</TableHead>
                          <TableHead>Teachers</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {classes.map((cls) =>
                          sections.map((section) => {
                            const mappings = getClassSectionMappings(cls._id, section._id, "teacher")
                            if (mappings.length === 0) return null
                            return (
                              <TableRow key={`${cls._id}-${section._id}`} className="hover:bg-gray-50 transition-colors">
                                <TableCell>{cls.name}-{section.name}</TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-2">
                                    {mappings.map((mapping) => (
                                      <Badge key={mapping._id} className={`${mapping.type === "class_teacher" ? "bg-green-500 hover:bg-green-600" : "bg-blue-500 hover:bg-blue-600"} text-white flex items-center gap-2 px-2 py-1`}>
                                        {mapping.user?.profileImage ? (
                                          <img src={mapping.user.profileImage || "/placeholder.svg"} alt={getFullName(mapping.user)} className="h-5 w-5 rounded-full object-cover" />
                                        ) : (
                                          <div className="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center">
                                            <span className="text-white text-xs font-medium">{getInitials(mapping.user)}</span>
                                          </div>
                                        )}
                                        <span>{getFullName(mapping.user)} - {mapping.subjects.filter((s) => s && s._id).map((s) => (typeof s === "string" ? s : s.name || "Unknown Subject")).join(", ")}</span>
                                        <button onClick={(e) => { e.stopPropagation(); handleDeleteMapping(mapping._id, "teacher", getFullName(mapping.user)) }} className="ml-1 hover:bg-red-500 rounded-full p-1 transition-all inline-flex items-center justify-center hover:scale-110" title="Delete assignment">
                                          <X className="h-4 w-4 text-white" />
                                        </button>
                                      </Badge>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )
                          }).filter(Boolean)
                        )}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>

              {viewMode === "teacher" && (
                <Pagination
                  currentPage={teacherPage}
                  totalItems={teacherTotal}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setTeacherPage}
                  onItemsPerPageChange={setItemsPerPage}
                  currentPageCount={sortedTeachers.length}
                  itemLabel="teachers"
                />
              )}
            </TabsContent>

            {/* Students Tab */}
            <TabsContent value="students" className="p-0">
              <div className="p-4 bg-white border-b border-gray-100">
                <div className="space-y-6">
                  <StudentSelect students={students} selectedStudent={selectedStudent} handleStudentChange={handleStudentChange} getFullName={getFullName} />

                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Assign Class</label>
                      <Select value={currentStudentClass} onValueChange={setCurrentStudentClass}>
                        <SelectTrigger className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                          <SelectValue placeholder="Choose Class" />
                        </SelectTrigger>
                        <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                          {classes.map((cls) => <SelectItem key={cls._id} value={cls._id}>{cls.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Assign Section</label>
                      <Select value={currentStudentSection} onValueChange={setCurrentStudentSection} disabled={!currentStudentClass || studentAvailableSections.length === 0}>
                        <SelectTrigger className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                          <SelectValue placeholder={!currentStudentClass ? "Select a class first" : studentAvailableSections.length === 0 ? "No sections available" : "Choose Section"} />
                        </SelectTrigger>
                        <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                          {studentAvailableSections.map((s) => <SelectItem key={s._id} value={s._id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-end">
                      <Button onClick={saveStudentMapping} className="w-full bg-[#0263cc] hover:bg-[#0263cc]/90 text-white" disabled={!selectedStudent || !currentStudentClass || !currentStudentSection || isLoading}>
                        {isLoading ? <Clock className="mr-2 h-4 w-4 animate-spin" /> : isSuccess ? <UserCheck className="mr-2 h-4 w-4" /> : null}
                        {isLoading ? "Saving..." : isSuccess ? "Saved Successfully" : "Save Assignment"}
                      </Button>
                    </div>
                  </div>

                  {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
                </div>
              </div>

              <div className="p-4 border-t border-gray-100">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Current Student Assignments</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">View by:</span>
                    <Select value={viewMode} onValueChange={setViewMode}>
                      <SelectTrigger className="w-[150px] border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                        <SelectItem value="student">Students</SelectItem>
                        <SelectItem value="class">Classes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  {viewMode === "student" ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 hover:bg-gray-50">
                          <TableHead className="cursor-pointer" onClick={() => handleSort("name")}>
                            <div className="flex items-center">Student Name{getSortIcon("name")}</div>
                          </TableHead>
                          <TableHead className="cursor-pointer" onClick={() => handleSort("class")}>
                            <div className="flex items-center">Class & Section{getSortIcon("class")}</div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedStudents.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                              {searchQuery ? "No students found matching your search" : "No students available"}
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedStudents.map((student) => {
                            const mappings = getStudentMappings(student._id)
                            return (
                              <TableRow key={student._id} className="hover:bg-gray-50 transition-colors">
                                <TableCell>
                                  <div className="flex items-center space-x-3">
                                    {student.profileImage ? (
                                      <img src={student.profileImage || "/placeholder.svg"} alt={getFullName(student)} className="h-8 w-8 rounded-full object-cover" />
                                    ) : (
                                      <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-600 text-sm font-medium">{getInitials(student)}</span>
                                      </div>
                                    )}
                                    <span className="text-[#0263cc] hover:text-[#0263cc]/80 cursor-pointer font-medium" onClick={() => handleStudentClick(student)}>
                                      {getFullName(student)}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {mappings.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                      {mappings.map((mapping) => (
                                        <Badge key={mapping._id} variant="secondary" className="flex items-center gap-1">
                                          <span>{mapping.class?.name || "Unknown"} - {mapping.section?.name || "Unknown"}</span>
                                          <button onClick={(e) => { e.stopPropagation(); handleDeleteMapping(mapping._id, "student", getFullName(student)) }} className="ml-1 hover:bg-red-500 rounded-full p-0.5 transition-all inline-flex items-center justify-center hover:scale-110" title="Delete assignment">
                                            <X className="h-4 w-4 text-white" />
                                          </button>
                                        </Badge>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500">No assignments</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            )
                          })
                        )}
                      </TableBody>
                    </Table>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50 hover:bg-gray-50">
                          <TableHead>Class</TableHead>
                          <TableHead>Section</TableHead>
                          <TableHead>Students</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {classes.map((cls) =>
                          sections.map((section) => {
                            const mappings = getClassSectionMappings(cls._id, section._id, "student")
                            return (
                              <TableRow key={`${cls._id}-${section._id}`} className="hover:bg-gray-50 transition-colors">
                                <TableCell>{cls.name}</TableCell>
                                <TableCell>{section.name}</TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-2">
                                    {mappings.length > 0 ? (
                                      mappings.map((mapping) => (
                                        <Badge key={mapping._id} variant="secondary" className="flex items-center gap-1">
                                          {mapping.user?.profileImage ? (
                                            <img src={mapping.user.profileImage || "/placeholder.svg"} alt={getFullName(mapping.user)} className="h-5 w-5 rounded-full object-cover" />
                                          ) : (
                                            <div className="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center">
                                              <span className="text-gray-600 text-xs font-medium">{getInitials(mapping.user)}</span>
                                            </div>
                                          )}
                                          <span>{getFullName(mapping.user)}</span>
                                          <button onClick={(e) => { e.stopPropagation(); handleDeleteMapping(mapping._id, "student", getFullName(mapping.user)) }} className="ml-1 hover:bg-red-500 rounded-full p-0.5 transition-all inline-flex items-center justify-center hover:scale-110" title="Delete assignment">
                                            <X className="h-4 w-4 text-white" />
                                          </button>
                                        </Badge>
                                      ))
                                    ) : (
                                      <span className="text-gray-500">No students assigned</span>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )
                          })
                        )}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>

              {viewMode === "student" && (
                <Pagination
                  currentPage={studentPage}
                  totalItems={studentTotal}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setStudentPage}
                  onItemsPerPageChange={setItemsPerPage}
                  currentPageCount={sortedStudents.length}
                  itemLabel="students"
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

export default ClassroomMapping
